import { ArrowLeft } from '@phosphor-icons/react';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { ActivityReportUnitsTable, DateRangeType } from '@plot/plot-api';

import { PriceChangesTable } from '@/features/activity-reports/activity-report/components/report-details/price-changes/PriceChangesTable';
import { useGetActivityReport } from '@/features/activity-reports/queries';
import { useArPriceChangesTable } from '@/features/activity-reports/queries/use-ar-price-changes-table';
import { useWorkspaceId } from '@/lib/contexts';
import { convertKeysToSnakeCase, geoJSONToEwkbHex } from '@/lib/utils';

import { TypePeriod } from '@/components/activity-reports/period-selector';

export const ActivityReportPriceChanges = () => {
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslations('ActivityReports.ActivityReport.UnitCategory');

  const search = location.search;

  const searchParams = new URLSearchParams(location.search);
  const selectedPeriod =
    (searchParams.get('period') as TypePeriod) || DateRangeType.WEEKS;

  const lastPeriodIncludes = searchParams.get('last_period_includes') as string;

  const activeDataset =
    (searchParams.get('activeDataset') as 'NEW_UNIT') ||
    'USED_UNIT' ||
    'NEW_UNIT';

  const { activityReportId } = useParams<{ activityReportId: string }>();

  const { data: activityReportData, isLoading: isLoadingActivityReportData } =
    useGetActivityReport({
      workspaceId,
      activityReportId,
    });

  const {
    data: priceChangesTableData,
    isLoading: isLoadingArPriceChangesTable,
  } = useArPriceChangesTable({
    workspaceId,
    activeDatasets: [activeDataset],
    filters: convertKeysToSnakeCase(activityReportData?.filters || {}),
    geometry: geoJSONToEwkbHex(
      activityReportData?.area.geometry as GeoJSON.Geometry
    ),
    areaId: activityReportData?.area.areaId,
    period: [selectedPeriod],
    lastPeriodIncludes,
  });

  const isLoading = isLoadingActivityReportData || isLoadingArPriceChangesTable;

  return (
    <div className="absolute z-40 h-full w-full overflow-y-auto bg-white px-4 py-10 dark:bg-superDark-bgWhite md:px-18">
      <div className="m-auto w-full max-w-[1300px]">
        <button
          className="cap-base mb-6 flex items-center gap-2"
          onClick={() => navigate(`../${search}`)}
        >
          <ArrowLeft />
          <span>{t('back')}</span>
        </button>
        <h2 className="hd-base mb-4">
          {t('priceChanges.title')} (
          {Array.isArray(priceChangesTableData)
            ? priceChangesTableData.length
            : 0}
          )
        </h2>
        {isLoading ? (
          <Skeleton height={300} />
        ) : (
          <div className="rounded border border-superLight-border dark:border-superDark-bgHeaderLight">
            <PriceChangesTable
              data={
                priceChangesTableData as ActivityReportUnitsTable[] | undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityReportPriceChanges;
